// i18n + currency + country helpers
import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import LanguageDetector from "i18next-browser-languagedetector";
import fr from "../locales/fr.json";
import en from "../locales/en.json";
import ar from "../locales/ar.json";

// MVP Phase 1 : FR uniquement, EN/AR conservés en code (Phase 5)
export const FEATURE_MULTI_LANG = false;

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources: { fr: { c: fr }, en: { c: en }, ar: { c: ar } },
    lng: FEATURE_MULTI_LANG ? undefined : "fr",
    fallbackLng: "fr",
    defaultNS: "c",
    supportedLngs: FEATURE_MULTI_LANG ? ["fr", "en", "ar"] : ["fr"],
    interpolation: { escapeValue: false },
    detection: { order: FEATURE_MULTI_LANG ? ["localStorage", "navigator"] : [], caches: ["localStorage"] },
  });

// Force FR si MVP — purge tout cache localStorage hérité d'une session multilingue
if (!FEATURE_MULTI_LANG && typeof window !== "undefined") {
  if (window.localStorage.getItem("i18nextLng") && window.localStorage.getItem("i18nextLng") !== "fr") {
    window.localStorage.setItem("i18nextLng", "fr");
  }
  if (i18n.language !== "fr") i18n.changeLanguage("fr");
}

// Apply RTL when lang is Arabic
const applyDir = (lang) => {
  if (typeof document === "undefined") return;
  document.documentElement.lang = lang;
  document.documentElement.dir = lang === "ar" ? "rtl" : "ltr";
};
applyDir(i18n.language || "fr");
i18n.on("languageChanged", applyDir);

export default i18n;

// Countries — MVP Phase 1 : France uniquement (CDC v1.0)
// Les autres pays sont conservés en code mais cachés derrière FEATURE_INTERNATIONAL.
export const FEATURE_INTERNATIONAL = false;

const ALL_COUNTRIES = [
  { code: "FR", name: "France", flag: "🇫🇷", currency: "EUR", symbol: "€", locale: "fr-FR", lang: "fr" },
  { code: "MA", name: "Maroc",  flag: "🇲🇦", currency: "MAD", symbol: "DH", locale: "fr-MA", lang: "fr" },
  { code: "TN", name: "Tunisie", flag: "🇹🇳", currency: "TND", symbol: "DT", locale: "fr-TN", lang: "fr" },
  { code: "AE", name: "Émirats arabes unis", flag: "🇦🇪", currency: "AED", symbol: "AED", locale: "en-AE", lang: "en" },
];

export const COUNTRIES = FEATURE_INTERNATIONAL ? ALL_COUNTRIES : ALL_COUNTRIES.filter((c) => c.code === "FR");

// Languages — MVP Phase 1 : FR uniquement
// Pour activer la détection auto multi-langue (Phase 5), passer FEATURE_MULTI_LANG=true (en haut du fichier).
export const AVAILABLE_LANGS = FEATURE_MULTI_LANG ? ["fr", "en", "ar"] : ["fr"];
const SUPPORTED_LANGS = ["fr", "en", "ar"];

export const findCountry = (code) => COUNTRIES.find((c) => c.code === code) || COUNTRIES[0];

export const formatMoney = (amount, countryCode = "FR") => {
  const c = findCountry(countryCode);
  if (amount == null) return "—";
  // For MAD/TND we display as "150 DH" or "75 DT" (more readable than ISO)
  if (c.currency === "MAD") return `${amount} DH`;
  if (c.currency === "TND") return `${amount} DT`;
  if (c.currency === "AED") return `AED ${amount}`;
  return new Intl.NumberFormat(c.locale, { style: "currency", currency: c.currency, maximumFractionDigits: 0 }).format(amount);
};

// Verifications: which fields contribute to the blue badge
export const VERIFICATION_FIELDS = ["diplome_ordre", "rpps", "identite"];
export const isFullyVerified = (v = {}) => VERIFICATION_FIELDS.every((k) => !!v[k]);

/**
 * Détection automatique de la langue à la première visite (façon Facebook).
 *  1. Si l'utilisateur a déjà choisi une langue → on respecte (localStorage i18nextLng)
 *  2. Sinon navigator.language → si commence par fr/en/ar, on l'utilise
 *  3. Sinon fallback API backend /api/geo/detect (IP geolocation Cloudflare)
 *  4. Sinon "fr" (défaut MVP Phase 1)
 *
 * Cette fonction est appelée au montage de <App />. Active uniquement si FEATURE_MULTI_LANG=true.
 */
export async function autoDetectLanguage(apiClient) {
  if (!FEATURE_MULTI_LANG) return; // MVP Phase 1 = FR forcé

  // 1. Choix utilisateur déjà fait → on respecte
  if (typeof window !== "undefined" && window.localStorage.getItem("i18nextLng")) return;

  // 2. navigator.language
  const nav = (typeof navigator !== "undefined" && (navigator.language || navigator.userLanguage)) || "";
  const navShort = nav.toLowerCase().split("-")[0];
  if (SUPPORTED_LANGS.includes(navShort)) {
    await i18n.changeLanguage(navShort);
    return;
  }

  // 3. IP geolocation via backend (Cloudflare CF-IPCountry → suggested_lang)
  try {
    const { data } = await apiClient.get("/geo/detect", { _skipAuth: true });
    if (data?.suggested_lang && SUPPORTED_LANGS.includes(data.suggested_lang)) {
      await i18n.changeLanguage(data.suggested_lang);
      return;
    }
  } catch {
    /* silent — on tombe sur le fallback FR */
  }

  // 4. Fallback FR
  await i18n.changeLanguage("fr");
}
